import type { AgentCursor } from "@/stores/canvas-store";
import { isDesktopInteractionTool } from "./desktop-interaction-cursor";

/** Finished presentations a cursor remembers, newest last. */
const FINISHED_PRESENTATIONS_KEPT = 16;
const FINISHED_CALLS_KEPT = 32;

function sameExecution(
  previous: AgentCursor | undefined,
  incoming: Partial<AgentCursor>,
) {
  return (
    !!previous &&
    (!incoming.executionId || incoming.executionId === previous.executionId)
  );
}

/**
 * A presentation update for a delivery that already finished on this cursor:
 * a replay (a reloaded window, a second controller) that must not show the
 * finished guide or its bubble again.
 */
export function isFinishedPresentationReplay(
  prev: AgentCursor | undefined,
  incoming: Partial<AgentCursor>,
): boolean {
  const deliveryId = incoming.presentationDeliveryId;
  return (
    incoming.presentation === "point" &&
    !!deliveryId &&
    !!prev?.finishedPresentationIds?.includes(deliveryId)
  );
}

/** The tool call a presentation speaks for: the call whose label the cursor
 *  shows when the presentation starts. A runtime update keeps it. */
export function presentedToolUseId(
  prev: AgentCursor | undefined,
  incoming: Partial<AgentCursor>,
): string | undefined {
  return incoming.presentation === "point" && incoming.presentationDeliveryId
    ? (prev?.toolUseId ?? prev?.presentedToolUseId)
    : prev?.presentedToolUseId;
}

/** An incoming label for a call whose presentation already ended never shows
 *  again (a replayed frame, a mirror catching up). */
export function withoutEndedCallLabel<T extends Partial<AgentCursor>>(
  prev: AgentCursor | undefined,
  incoming: T,
): T {
  const replay =
    incoming.toolCall &&
    incoming.toolUseId &&
    sameExecution(prev, incoming) &&
    (incoming.toolUseId === prev?.endedToolUseId ||
      prev?.completedToolUseIds?.includes(incoming.toolUseId));
  if (!replay) return incoming;
  const status = { ...incoming };
  delete status.toolCall;
  delete status.toolCallLabel;
  delete status.toolFailed;
  delete status.toolUseId;
  delete status.toolName;
  delete status.readsOnly;
  return status;
}

/** Keep ordinary tool status between steps, including after its result.
 *  Completion receipts still block replayed labels; desktop actions and
 *  questions end with their own call instead of lingering as progress. */
export function finishCursorToolCalls(
  merged: AgentCursor,
  incoming: Partial<AgentCursor>,
  previous?: AgentCursor,
): void {
  if (!sameExecution(previous, incoming)) {
    merged.endedToolUseId = undefined;
    merged.toolUseId = incoming.toolUseId;
    if (!incoming.toolCall) {
      merged.toolCall = undefined;
      merged.toolCallLabel = undefined;
    }
  }
  const completed = [
    ...new Set([
      ...(sameExecution(previous, incoming)
        ? (previous?.completedToolUseIds ?? [])
        : []),
      ...(incoming.completedToolUseIds ?? []),
    ]),
  ].slice(-FINISHED_CALLS_KEPT);
  merged.completedToolUseIds = completed.length ? completed : undefined;
  if (
    merged.frozen ||
    (merged.toolUseId &&
      completed.includes(merged.toolUseId) &&
      (isDesktopInteractionTool(merged.toolName, merged.readsOnly) ||
        isCursorQuestionTool(merged)))
  ) {
    merged.toolCall = undefined;
    merged.toolCallLabel = undefined;
    merged.toolFailed = undefined;
  }
}

export function isCursorQuestionTool(cursor: AgentCursor): boolean {
  return (
    /(?:^|__|\.)(?:ask_user|askuserquestion|request_user_input|request_user_input_async)$/i.test(
      cursor.toolName ?? "",
    ) ||
    (!cursor.toolName && !!cursor.toolCall?.startsWith("Asking a question"))
  );
}

/**
 * What a cursor drops when its presentation ends: the presented call's label
 * (all its steps are done; a newer call's label stays). A completed delivery
 * joins the finished ones so a replay of it is ignored; a cancelled one may
 * still be played again by a later playback attempt.
 */
export function endPresentation(
  cursor: AgentCursor,
  completedDeliveryId: string | undefined,
): Partial<AgentCursor> {
  const callId = cursor.presentedToolUseId;
  const callEnded = !!callId && cursor.toolUseId === callId;
  return {
    presentedToolUseId: undefined,
    ...(callEnded
      ? {
          toolCall: undefined,
          toolCallLabel: undefined,
          toolFailed: undefined,
          endedToolUseId: callId,
        }
      : {}),
    ...(completedDeliveryId
      ? {
          finishedPresentationIds: [
            ...(cursor.finishedPresentationIds ?? []).filter(
              (id) => id !== completedDeliveryId,
            ),
            completedDeliveryId,
          ].slice(-FINISHED_PRESENTATIONS_KEPT),
        }
      : {}),
  };
}
